import { useState, useEffect, useRef } from 'react';
import { Sparkles, Star, MapPin, Lock, ArrowRight, Zap, Clock, BookmarkPlus, Check,
         SlidersHorizontal, MessageSquare, Search, HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import PageHelp from '../components/PageHelp';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EXAMPLE_JDS = [
  { label: 'Senior Womenswear Designer', text: `Senior Womenswear Designer based in Milan. 6+ years luxury RTW experience. Expert in CLO 3D and Adobe Illustrator, strong pattern making skills. Minimalist avant-garde aesthetic, sustainable approach. Available immediately, open to hybrid work.` },
  { label: 'Creative Director', text: `Creative Director for a growing streetwear brand in London. 10+ years fashion experience, menswear and unisex collections, proven team leadership. Photoshop, InDesign, brand strategy. Remote-friendly. Edgy digital-native aesthetic.` },
  { label: 'Fashion Photographer', text: `Fashion Photographer based in Florence, Italy. 5+ years editorial and commercial photography experience. Expert in studio and location shooting, strong retouching skills in Lightroom and Capture One. Luxury and editorial aesthetic. Available immediately, open to travel.` },
];

// ── Score bar ─────────────────────────────────────────────────────────────────
const ScoreBar = ({ label, value, max, color }) => (
  <div>
    <div className="flex justify-between text-xs mb-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}/{max}</span>
    </div>
    <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
      <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${(value / max) * 100}%` }} />
    </div>
  </div>
);

// ── Match card ────────────────────────────────────────────────────────────────
const MatchCard = ({ candidate, rank, jd, onAddToPipeline, onOutreach, onInterviewQ, onFindSimilar }) => {
  const navigate = useNavigate();
  const [showDetail, setShowDetail] = useState(false);
  const [outreachMsg, setOutreachMsg] = useState(null);
  const [interviewQs, setInterviewQs] = useState(null);
  const [selectedQs, setSelectedQs] = useState(new Set());
  const [savingQs, setSavingQs] = useState(false);
  const [savedQs, setSavedQs] = useState(false);
  const [loadingOutreach, setLoadingOutreach] = useState(false);
  const [loadingInterviewQ, setLoadingInterviewQ] = useState(false);
  const [addedToPipeline, setAddedToPipeline] = useState(false);

  const score = candidate.match_score;
  const scoreColor = score >= 70 ? 'text-green-700 bg-green-50 border-green-200'
    : score >= 50 ? 'text-yellow-700 bg-yellow-50 border-yellow-200'
    : 'text-muted-foreground bg-secondary border-border';

  const handleOutreach = async () => {
    setLoadingOutreach(true);
    try {
      const res = await authFetch(`${BACKEND}/api/matchmaker/outreach`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: candidate.id, job_description: jd }),
      });
      const data = await res.json();
      if (data.success) setOutreachMsg(data.message);
    } catch {} finally { setLoadingOutreach(false); }
  };

  const handleInterviewQ = async () => {
    setLoadingInterviewQ(true);
    try {
      const res = await authFetch(`${BACKEND}/api/matchmaker/interview-questions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: candidate.id, job_description: jd }),
      });
      const data = await res.json();
      if (data.success) {
        setInterviewQs(data.questions);
        // Default: all selected
        setSelectedQs(new Set(data.questions.map((_, i) => i)));
        setSavedQs(false);
      }
    } catch {} finally { setLoadingInterviewQ(false); }
  };

  const handleSaveQuestions = async () => {
    if (!interviewQs || selectedQs.size === 0) return;
    setSavingQs(true);
    try {
      const toSave = interviewQs.filter((_, i) => selectedQs.has(i));
      const res = await authFetch(`${BACKEND}/api/matchmaker/save-interview-questions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: candidate.id, questions: toSave, job_description: jd }),
      });
      const data = await res.json();
      if (data.success) { setSavedQs(true); toast.success(`${toSave.length} question(s) saved to pipeline`); }
      else toast.error('Failed to save questions');
    } catch { toast.error('Failed to save questions'); } finally { setSavingQs(false); }
  };

  const toggleQ = (i) => {
    setSelectedQs(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const handleAddPipeline = async () => {
    await onAddToPipeline([candidate.id]);
    setAddedToPipeline(true);
  };

  return (
    <div className="bg-white border border-border hover:shadow-sm transition-shadow">
      <div className="flex gap-4 p-4">
        <div className="flex-shrink-0 w-7 text-center pt-1">
          <span className={`text-base font-bold ${rank <= 3 ? 'text-foreground' : 'text-muted-foreground'}`}>#{rank}</span>
        </div>
        <div className="w-12 h-16 bg-secondary flex-shrink-0 overflow-hidden cursor-pointer" onClick={() => navigate(`/candidate/${candidate.id}`)}>
          {candidate.photo_url && <img src={candidate.photo_url} alt={candidate.full_name} className="w-full h-full object-cover" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <button onClick={() => navigate(`/candidate/${candidate.id}`)} className="font-semibold text-sm hover:underline text-left">{candidate.full_name}</button>
              <p className="text-xs text-muted-foreground">{candidate.seniority} {candidate.core_role}</p>
              <div className="flex items-center gap-1 mt-0.5 text-xs text-muted-foreground">
                <MapPin className="w-3 h-3" />{candidate.location_city}, {candidate.location_country}
                {candidate.overall_ranking && <><Star className="w-3 h-3 ml-2 fill-foreground" />{candidate.overall_ranking}</>}
              </div>
            </div>
            <div className={`flex-shrink-0 px-2.5 py-1.5 border text-center min-w-[60px] ${scoreColor}`}>
              <p className="text-lg font-bold">{score}</p>
              <p className="text-xs">match</p>
            </div>
          </div>

          {/* Reasons */}
          <div className="flex flex-wrap gap-1 mt-2">
            {(candidate.match_reasons || []).map((r, i) => (
              <span key={i} className="text-xs bg-secondary border border-border px-1.5 py-0.5 text-muted-foreground">{r}</span>
            ))}
          </div>

          {/* GPT explanation */}
          {candidate.gpt_explanation && (
            <p className="text-xs text-muted-foreground mt-2 italic border-l-2 border-purple-300 pl-2">{candidate.gpt_explanation}</p>
          )}
        </div>
      </div>

      {/* Score breakdown */}
      <div className="border-t border-border px-4 py-2.5 grid grid-cols-4 gap-2">
        <ScoreBar label="Skills" value={candidate.score_breakdown?.hard_skills || 0} max={25} color="bg-blue-400" />
        <ScoreBar label="Semantic" value={candidate.score_breakdown?.semantic || 0} max={40} color="bg-purple-400" />
        <ScoreBar label="Social" value={candidate.score_breakdown?.social_proof || 0} max={20} color="bg-green-400" />
        <ScoreBar label="Logistics" value={candidate.score_breakdown?.logistics || 0} max={15} color="bg-yellow-400" />
      </div>

      {/* Action row */}
      <div className="border-t border-border px-4 py-2 flex items-center gap-2 flex-wrap">
        <button onClick={handleAddPipeline} disabled={addedToPipeline}
          className={`flex items-center gap-1 text-xs px-2.5 py-1.5 border transition-colors ${addedToPipeline ? 'border-green-400 text-green-600 bg-green-50' : 'border-border text-muted-foreground hover:border-foreground hover:text-foreground'}`}>
          {addedToPipeline ? <><Check className="w-3 h-3" />Saved</> : <><BookmarkPlus className="w-3 h-3" />Save</>}
        </button>
        <button onClick={() => { setShowDetail(!showDetail); if (!showDetail && !candidate.gpt_strengths?.length) {} }}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-border text-muted-foreground hover:border-foreground hover:text-foreground transition-colors">
          {showDetail ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />} Details
        </button>
        <button onClick={handleOutreach} disabled={loadingOutreach}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-border text-muted-foreground hover:border-foreground hover:text-foreground transition-colors">
          <MessageSquare className="w-3 h-3" /> {loadingOutreach ? 'Generating...' : 'Outreach'}
        </button>
        <button onClick={handleInterviewQ} disabled={loadingInterviewQ}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-border text-muted-foreground hover:border-foreground hover:text-foreground transition-colors">
          <HelpCircle className="w-3 h-3" /> {loadingInterviewQ ? 'Generating...' : 'Interview Qs'}
        </button>
        <button onClick={() => onFindSimilar(candidate.id)}
          className="flex items-center gap-1 text-xs px-2.5 py-1.5 border border-border text-muted-foreground hover:border-foreground hover:text-foreground transition-colors">
          <Search className="w-3 h-3" /> Find Similar
        </button>
      </div>

      {/* Expandable details */}
      {showDetail && (
        <div className="border-t border-border p-4 space-y-3 bg-secondary/20">
          {candidate.gpt_strengths?.length > 0 && (
            <div>
              <p className="text-xs font-semibold mb-1.5">GPT Key Strengths</p>
              <ul className="space-y-1">
                {candidate.gpt_strengths.map((s, i) => <li key={i} className="text-xs text-muted-foreground flex gap-2"><Check className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />{s}</li>)}
              </ul>
            </div>
          )}
          {candidate.gpt_concern && (
            <div>
              <p className="text-xs font-semibold mb-1">Consideration</p>
              <p className="text-xs text-muted-foreground">{candidate.gpt_concern}</p>
            </div>
          )}
        </div>
      )}

      {/* Outreach message */}
      {outreachMsg && (
        <div className="border-t border-border p-4 bg-blue-50">
          <p className="text-xs font-semibold mb-2 text-blue-800">Generated Outreach Message</p>
          <p className="text-xs text-blue-700 whitespace-pre-wrap leading-relaxed">{outreachMsg}</p>
          <button onClick={() => navigator.clipboard.writeText(outreachMsg)}
            className="mt-2 text-xs text-blue-600 underline hover:text-blue-800">Copy to clipboard</button>
        </div>
      )}

      {/* Interview questions */}
      {interviewQs && (
        <div className="border-t border-border p-4 bg-purple-50">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-purple-800">Interview Questions</p>
            <span className="text-xs text-purple-500">{selectedQs.size} selected</span>
          </div>
          <ol className="space-y-2 mb-3">
            {interviewQs.map((q, i) => (
              <li key={i}
                className={`flex items-start gap-2 text-xs cursor-pointer p-1.5 rounded transition-colors ${selectedQs.has(i) ? 'bg-purple-100' : 'opacity-50'}`}
                onClick={() => toggleQ(i)}
              >
                <div className={`mt-0.5 w-3.5 h-3.5 flex-shrink-0 border rounded-sm flex items-center justify-center ${selectedQs.has(i) ? 'bg-purple-600 border-purple-600' : 'border-purple-300'}`}>
                  {selectedQs.has(i) && <Check className="w-2.5 h-2.5 text-white" strokeWidth={2.5} />}
                </div>
                <div>
                  <span className="text-purple-600 font-medium">[{q.category}]</span>{' '}
                  <span className="text-purple-800">{q.question}</span>
                </div>
              </li>
            ))}
          </ol>
          <div className="flex gap-2 items-center">
            <button
              onClick={handleSaveQuestions}
              disabled={savingQs || savedQs || selectedQs.size === 0}
              className={`flex items-center gap-1 text-xs px-3 py-1.5 border transition-colors ${savedQs ? 'border-green-400 text-green-700 bg-green-50' : 'border-purple-400 text-purple-700 bg-white hover:bg-purple-100'}`}
              data-testid={`save-interview-qs-${candidate.id}`}
            >
              {savedQs ? <><Check className="w-3 h-3" /> Saved to pipeline</> : savingQs ? 'Saving...' : <><BookmarkPlus className="w-3 h-3" /> Save to pipeline</>}
            </button>
            <button onClick={() => navigator.clipboard.writeText(interviewQs.map(q => `[${q.category}] ${q.question}`).join('\n'))}
              className="text-xs text-purple-500 underline hover:text-purple-700">Copy all</button>
          </div>
          <p className="text-xs text-purple-400 mt-2 italic">Saved questions will be available for outreach in a future update</p>
        </div>
      )}
    </div>
  );
};

// ── Weight sliders (H) ────────────────────────────────────────────────────────
const WeightSliders = ({ weights, onChange }) => {
  const DIMS = [
    { key: 'hard_skills', label: 'Hard Skills', color: 'text-blue-600' },
    { key: 'semantic', label: 'Semantic Style', color: 'text-purple-600' },
    { key: 'social_proof', label: 'Social Proof', color: 'text-green-600' },
    { key: 'logistics', label: 'Logistics', color: 'text-yellow-600' },
  ];
  return (
    <div className="border border-amber-200 bg-amber-50 p-4 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">⚖️ Adjust Scoring Weights</p>
      {DIMS.map(d => (
        <div key={d.key}>
          <div className="flex justify-between text-xs mb-1">
            <span className={`font-medium ${d.color}`}>{d.label}</span>
            <span className="text-muted-foreground">{weights[d.key].toFixed(1)}×</span>
          </div>
          <input type="range" min="0.1" max="3" step="0.1" value={weights[d.key]}
            onChange={e => onChange({ ...weights, [d.key]: parseFloat(e.target.value) })}
            className="w-full accent-amber-600" />
        </div>
      ))}
      <button onClick={() => onChange({ hard_skills: 1.0, semantic: 1.0, social_proof: 1.0, logistics: 1.0 })}
        className="text-xs text-amber-600 underline">Reset to default</button>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────
const AIMatchmaker = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jd, setJd] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const [showWeights, setShowWeights] = useState(false);
  const [weights, setWeights] = useState({ hard_skills: 1.0, semantic: 1.0, social_proof: 1.0, logistics: 1.0 });

  const isIntelligence = user?.subscription_plan === 'intelligence';

  useEffect(() => {
    if (isIntelligence) {
      authFetch(`${BACKEND}/api/matchmaker/history`, {})
        .then(r => r.json()).then(d => { if (d.success) setHistory(d.data); }).catch(() => {});
    }
  }, [isIntelligence]);

  const handleMatch = async () => {
    if (!jd.trim() || jd.length < 50) { setError('Please provide a more detailed job description (min 50 characters).'); return; }
    setError(''); setLoading(true); setResults(null);
    try {
      const res = await authFetch(`${BACKEND}/api/matchmaker`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_description: jd, top_n: 25, weights }),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : {};
      if (!res.ok) throw new Error(data.detail || 'Matching failed');
      setResults(data);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const handleAddToPipeline = async (candidateIds) => {
    await authFetch(`${BACKEND}/api/matchmaker/add-to-pipeline`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ candidate_ids: candidateIds, stage: 'saved' }),
    });
  };

  const handleAddAllTop5 = async () => {
    if (!results?.results) return;
    const top5 = results.results.slice(0, 5).map(r => r.id);
    await handleAddToPipeline(top5);
    toast.success('Top 5 candidates added to pipeline');
  };

  const handleFindSimilar = async (candidateId) => {
    setLoading(true); setResults(null); setError('');
    try {
      const res = await authFetch(`${BACKEND}/api/matchmaker/find-similar`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_id: candidateId, top_n: 10 }),
      });
      const data = await res.json();
      if (data.success) setResults({ ...data, total_scored: data.returned, is_similar: true });
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const matchmakerSEO = (
    <SEO
      title="AI DNA Matchmaker — Find Your Perfect Fashion Hire"
      description="Paste a job description and let FashionSpyder's proprietary AI rank every fashion professional by skills, style DNA, social proof and logistical fit. Powered by GPT-4o and semantic vector matching."
      path="/ai-matchmaker"
    />
  );

  // ── Upgrade gate ────────────────────────────────────────────────────────────
  if (!user) return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      {matchmakerSEO}
      <div className="max-w-md text-center space-y-4">
        <Zap className="w-12 h-12 mx-auto text-muted-foreground" />
        <h2 className="text-2xl font-bold">Sign in to use AI Matchmaker</h2>
        <p className="text-muted-foreground">Available on the INTELLIGENCE plan.</p>
        <Button onClick={() => navigate('/login')} className="rounded-none bg-foreground text-background w-full">Sign In</Button>
      </div>
    </div>
  );

  if (!isIntelligence) return (
    <div className="min-h-screen bg-white">
      {matchmakerSEO}
      <section className="py-20 lg:py-28 px-6 lg:px-20 border-b border-border">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center gap-3 mb-4"><Sparkles className="w-8 h-8" /><h1 className="text-5xl lg:text-6xl font-bold">AI Matchmaker</h1></div>
          <p className="text-lg text-muted-foreground max-w-2xl">Paste a job description. Get the top 25 candidates ranked by GPT-powered AI scoring.</p>
        </div>
      </section>
      <section className="py-20 px-6 lg:px-20">
        <div className="max-w-[800px] mx-auto border border-border p-10 text-center space-y-6">
          <Lock className="w-12 h-12 mx-auto text-muted-foreground" />
          <div>
            <h2 className="text-2xl font-bold mb-2">INTELLIGENCE Plan Required</h2>
            <p className="text-muted-foreground">AI Matchmaker uses GPT-4o-mini to understand your JD and rank candidates with human-level insight. Available from €139/mo annual or €199/mo monthly.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm text-left max-w-sm mx-auto">
            {['GPT-powered JD parsing', 'AI final ranking + explanations', 'Personalised outreach messages', 'Interview question generator', 'Find similar candidates', 'Adjustable scoring weights'].map(f => (
              <div key={f} className="flex items-center gap-2 text-muted-foreground"><Sparkles className="w-3.5 h-3.5 flex-shrink-0" />{f}</div>
            ))}
          </div>
          <Button onClick={() => navigate('/pricing')} className="rounded-none bg-foreground text-background gap-2">
            <Zap className="w-4 h-4" /> Upgrade to INTELLIGENCE <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>
    </div>
  );

  // ── Full UI ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-white">
      {matchmakerSEO}
      <section className="py-20 lg:py-24 px-6 lg:px-20 border-b border-border">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-7 h-7" />
            <h1 className="text-4xl lg:text-5xl font-bold">AI Matchmaker</h1>
            <span className="text-xs bg-purple-100 text-purple-700 border border-purple-200 px-2 py-0.5 font-medium">INTELLIGENCE</span>
            <PageHelp pageKey="matchmaker" className="ml-auto" />
          </div>
          <p className="text-muted-foreground max-w-2xl">GPT-powered JD parsing + semantic ranking. Get AI explanations, outreach messages, and interview questions for every match.</p>
          <p className="text-xs text-muted-foreground/80 max-w-2xl mt-3 border-l-2 border-purple-200 pl-3 italic">
            StyleDNA and match scores are generated by FashionSpyder's proprietary algorithm combined with AI.
            AI can make mistakes — these results are decision-support tools meant to assist, not replace, your
            team's judgement. Always review candidates before deciding.
          </p>
        </div>
      </section>

      <section className="py-12 px-6 lg:px-20">
        <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8">

          {/* Input panel */}
          <div className="lg:col-span-2 space-y-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wide mb-2">Job Description</label>
              <textarea value={jd} onChange={e => setJd(e.target.value)}
                placeholder="Paste your full job description here. Include role, skills, location, experience level, and style."
                className="w-full border border-border p-4 text-sm resize-none rounded-none focus:outline-none focus:border-foreground"
                rows={11} />
              <p className="text-xs text-muted-foreground mt-1">{jd.length} chars · min 50</p>
            </div>

            {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

            <Button onClick={handleMatch} disabled={loading || jd.length < 50}
              className="w-full rounded-none bg-foreground text-background hover:bg-foreground/80 gap-2 py-4">
              <Sparkles className="w-4 h-4" />
              {loading ? 'AI is analysing candidates...' : 'Find Best Matches'}
            </Button>

            {/* Weight sliders toggle (H) */}
            <button onClick={() => setShowWeights(!showWeights)}
              className="w-full flex items-center justify-between text-xs text-muted-foreground border border-border px-3 py-2 hover:border-foreground transition-colors">
              <span className="flex items-center gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5" /> Adjust Scoring Weights</span>
              {showWeights ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {showWeights && <WeightSliders weights={weights} onChange={setWeights} />}

            {/* Examples */}
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Try an example:</p>
              <div className="space-y-1.5">
                {EXAMPLE_JDS.map(ex => (
                  <button key={ex.label} onClick={() => setJd(ex.text)}
                    className="w-full text-left text-xs border border-border px-3 py-2 hover:border-foreground hover:bg-secondary transition-colors">{ex.label} →</button>
                ))}
              </div>
            </div>

            {/* Scoring legend */}
            <div className="border border-border p-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide mb-3">Scoring Algorithm (v3 — GPT)</p>
              {[
                { label: 'Hard Skills', max: 25, color: 'bg-blue-400', desc: 'Structured JD-parsed skill matching' },
                { label: 'Semantic Style', max: 40, color: 'bg-purple-400', desc: 'Fashion vocabulary cosine similarity' },
                { label: 'Social Proof', max: 20, color: 'bg-green-400', desc: 'Ranking, authority, endorsements' },
                { label: 'Logistics', max: 15, color: 'bg-yellow-400', desc: 'Location match + availability' },
              ].map(s => (
                <div key={s.label} className="flex items-center gap-2.5 text-xs">
                  <div className={`w-2 h-2 rounded-sm flex-shrink-0 ${s.color}`} />
                  <span className="font-medium w-24">{s.label}</span>
                  <span className="text-muted-foreground flex-1">{s.desc}</span>
                  <span className="text-muted-foreground font-medium">{s.max}pts</span>
                </div>
              ))}
              <p className="text-xs text-purple-700 border-t border-border pt-2 mt-2">+ GPT final ranking adjusts top 15 scores with human-level fashion insight</p>
            </div>

            {/* Prompting guide */}
            <div className="border border-amber-200 bg-amber-50 p-4 space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-800 mb-2">✍️ How to get the best results</p>
              <div className="space-y-1 text-xs text-amber-700">
                <p><strong>📍 Location:</strong> Name the city — e.g. <em>"based in Milan"</em></p>
                <p><strong>🎯 Role:</strong> Be specific — e.g. <em>"Senior Womenswear Designer"</em></p>
                <p><strong>🛠 Skills:</strong> List tools — e.g. <em>"CLO 3D, Illustrator"</em></p>
                <p><strong>🎨 Style:</strong> Include aesthetic — e.g. <em>"minimalist luxury"</em></p>
                <p><strong>📅 Availability:</strong> <em>"available immediately"</em></p>
              </div>
            </div>
          </div>

          {/* Results panel */}
          <div className="lg:col-span-3">
            {loading && (
              <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <div className="w-10 h-10 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
                <div className="text-center">
                  <p className="text-sm font-medium">AI is working...</p>
                  <p className="text-xs text-muted-foreground mt-1">Parsing JD → Scoring candidates → GPT final ranking</p>
                </div>
              </div>
            )}

            {!loading && !results && (
              <div className="flex flex-col items-center justify-center h-64 text-center space-y-3 border border-dashed border-border">
                <Sparkles className="w-10 h-10 text-muted-foreground" />
                <p className="font-semibold text-muted-foreground">Results will appear here</p>
                <p className="text-sm text-muted-foreground max-w-xs">Paste a job description and click "Find Best Matches"</p>
              </div>
            )}

            {results && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold">{results.is_similar ? `Similar to reference` : `Top ${results.returned} matches`}</h2>
                    <p className="text-xs text-muted-foreground">
                      Scored {results.total_scored} candidates
                      {results.role_filter_applied && ` (filtered from ${results.total_in_db} by role)`}
                      {' · '}GPT-ranked top 15
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!results.is_similar && (
                      <Button onClick={handleAddAllTop5} variant="outline" className="rounded-none text-xs h-8 gap-1">
                        <BookmarkPlus className="w-3 h-3" /> Add Top 5
                      </Button>
                    )}
                    <Button onClick={() => setResults(null)} variant="ghost" className="text-sm rounded-none h-8">New search</Button>
                  </div>
                </div>
                <div className="space-y-3">
                  {results.results.map((c, i) => (
                    <MatchCard key={c.id} candidate={c} rank={i + 1} jd={jd}
                      onAddToPipeline={handleAddToPipeline}
                      onFindSimilar={handleFindSimilar} />
                  ))}
                </div>
              </div>
            )}

            {/* Past searches */}
            {!results && history.length > 0 && (
              <div className="mt-8">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">Recent Searches</p>
                <div className="space-y-2">
                  {history.slice(0, 5).map(h => (
                    <div key={h.id} className="flex items-center justify-between border border-border px-4 py-3 text-sm">
                      <p className="text-muted-foreground truncate max-w-xs">{h.job_description?.slice(0, 80)}...</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground flex-shrink-0">
                        <Clock className="w-3 h-3" />{new Date(h.created_at).toLocaleDateString()}<span>{h.results_count} results</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIMatchmaker;
